import { FC, useMemo } from "react";

import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import { useLocalization } from "@/hooks";
import { QuestionResults, Section } from "@/types/resources";
import ClaimTestReport, { ClaimTestReportData } from "./ClaimTestReport";
import { cookTopBoxScoring } from "./_topBoxScoring";

interface ClaimTestSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/** Templated claim-test sections carry believability / uniqueness /
    motivation in a fixed order. Translated labels live here; claim
    text still comes from `section.stimuli`. */
const ClaimTestSectionResults: FC<ClaimTestSectionResultsProps> = ({
  data,
  section,
}) => {
  const { i18n } = useLocalization();
  const labels = useUseCaseLabels(section.use_case);

  const reportData: ClaimTestReportData = useMemo(() => {
    const metricLabels = [
      i18n.t("useCasePage.claimTest.sampleReport.metrics.believability"),
      i18n.t("useCasePage.claimTest.sampleReport.metrics.uniqueness"),
      i18n.t("useCasePage.claimTest.sampleReport.metrics.motivation"),
    ];
    const scored = cookTopBoxScoring(data, section, metricLabels, (i) =>
      labels.stimulusPlaceholder(i + 1)
    );
    return {
      claims: scored.entities,
      metrics: scored.metrics,
      perCellSampleSize: scored.perCellSampleSize,
      respondentCount: scored.respondentCount,
      topBoxCounts: scored.topBoxCounts,
    };
  }, [data, section, i18n, labels]);

  return <ClaimTestReport data={reportData} />;
};

export default ClaimTestSectionResults;
